import React, { useEffect, useState, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Grid, Card, CardContent, IconButton, MenuItem,
    FormControl, InputLabel, Select, Autocomplete,Typography
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { updateBulkItems, itemList } from "../services/api";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";

const BulkUpdateModal = ({ visible, onClose, getAllUpdatedBrands }) => {
    const itemInputRef = useRef(null);
    const { user } = useUser();
    const [brand, setBrand] = useState(null);
    const [formData, setFormData] = useState({
        itemId: "",
        itemsUnits: "",
        issuePrice: "",
        mrp: "",
    });
    const [items, setItems] = useState([]);
    const [itemsList, setItemsList] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Fetch all items
    const fetchItemList = async () => {
        try {
            const response = await itemList(user?.orgId);
            if (response?.status === 200 && Array.isArray(response.data)) {
                const sortedData = response.data.sort((a, b) => Number(a.itemId) - Number(b.itemId));
                setItemsList(sortedData);
            } else {
                setItemsList([]);
            }
        } catch (error) {
            console.error("Failed to fetch items:", error);
            setItemsList([]);
        }
    };

    const getUniqueItems = (items) => {
        const uniqueItems = Array.from(
            new Set(items.map((item) => item.itemId))
        ).map((itemId) => items.find((item) => item.itemId === itemId));
        return uniqueItems;
    };

    const handleAdd = () => {
        if (!formData.itemId || !formData.itemsUnits || !formData.issuePrice || !formData.mrp) {
            toast.error("Please fill all fields!");
            return;
        }

        setItems((prev) => [...prev, { ...formData }]);

        // Reset form fields
        setFormData({ itemId: "", itemsUnits: "", issuePrice: "", mrp: "" });

        // Ensure Autocomplete resets properly
        setBrand(null);

        // Focus back to the Autocomplete input after a slight delay
        setTimeout(() => {
            if (itemInputRef.current) {
                itemInputRef.current.focus();
            }
        }, 100);
    };


    const handleUpdateAll = async () => {
        if (items.length === 0) {
            toast.warning("No items to update.");
            return;
        }

        try {
            const response = await updateBulkItems(items);
            if (response?.status === 200 || response?.data?.success) {
                toast.success("Items Prices updated successfully!");
                getAllUpdatedBrands(user?.orgId);
                setItems([]);
            } else {
                throw new Error("Invalid response");
            }
            // Focus back to the Item input field
            if (itemInputRef.current) {
                setTimeout(() => itemInputRef.current.focus(), 100); // Slight delay to ensure UI updates before focusing
            }
        } catch (error) {
            console.error("Update failed", error);
            toast.error("Failed to update item prices.");
        }
    };

    const handleDelete = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleItemChange = (selectedItemId) => {
        const selectedItem = itemsList.find((item) => item.itemId === selectedItemId);
        if (selectedItem) {
            setFormData((prev) => ({
                ...prev,
                itemId: selectedItem.itemId,
                itemsUnits: prev.itemsUnits || "",
                issuePrice: prev.issuePrice || "",
                mrp: prev.mrp || ""
            }));
        }
    };

    useEffect(() => {
        fetchItemList();
    }, []);

    return (
        <Dialog open={visible} onClose={onClose} fullWidth
            sx={{ "& .MuiDialog-paper": { width: "90%", maxWidth: "90%", height: "90vh" } }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
        >

            <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 20 }}>
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Grid container spacing={2} sx={{ marginTop: "30px" }}>
                    <Grid item>
                        <Card sx={{ height: "350px", width: "400px", padding: "15px 20px", boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.5)", borderRadius: "8px" }}>
                            <CardContent>
                                <Typography variant="h5" color="primary" marginBottom="15px">
                                    Bulk Updates
                                </Typography>

                                <Autocomplete
                                    options={getUniqueItems(itemsList)}
                                    getOptionLabel={(item) => `${item.itemId}`}
                                    value={itemsList.find((item) => item.itemId === formData.itemId) || null}
                                    onChange={(e, selectedItem) => {
                                        if (selectedItem) {
                                            setBrand(selectedItem);
                                            handleItemChange(selectedItem.itemId);
                                        }
                                    }}
                                    sx={{ marginBottom: "12px" }}
                                    renderInput={(params) => <TextField {...params} label="Search Items" fullWidth size="small" inputRef={itemInputRef} />}
                                />


                                <FormControl fullWidth required>
                                    <InputLabel>Quantity</InputLabel>
                                    <Select
                                        value={formData.itemsUnits}
                                        onChange={handleChange}
                                        label="Units"
                                        name="itemsUnits"
                                        size="small"
                                    >
                                        {["50ml", "60ml", "90ml", "180ml", "200ml", "275ml", "330ml", "375ml", "500ml", "650ml", "700ml", "750ml", "1000ml", "2000ml"].map((q, index) => (
                                            <MenuItem key={index} value={q}>{q}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField fullWidth margin="dense" label="Issue Price" name="issuePrice" value={formData.issuePrice} onChange={handleChange} size="small" required />
                                <TextField fullWidth margin="dense" label="MRP" name="mrp" value={formData.mrp} onChange={handleChange} size="small" required />
                                <div style={{ display: "flex", justifyContent: "end", marginTop: "10px" }}>
                                    <Button onClick={handleAdd} variant="contained" color="primary" sx={{ textTransform: "none" }} >Add</Button>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={8}>
                        <TableContainer component={Paper} sx={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {["Item ID", "Units", "Issue Price", "MRP", "Action"].map((head, index) => (
                                            <TableCell key={index} scope="col" sx={{ background: "#133E87", color: "white", textAlign: "center" }}>{head}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length > 0 ? items.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ textAlign: "center" }}>{item.itemId}</TableCell>
                                            <TableCell sx={{ textAlign: "center" }}>{item.itemsUnits}</TableCell>
                                            <TableCell sx={{ textAlign: "center" }}>{item.issuePrice}</TableCell>
                                            <TableCell sx={{ textAlign: "center" }}>{item.mrp}</TableCell>
                                            <TableCell sx={{ textAlign: "center" }}><IconButton onClick={() => handleDelete(index)} color="error"><Delete /></IconButton></TableCell>
                                        </TableRow>
                                    )) : <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: "center", color: "#0A3981" }}>
                                            No records to update...!
                                        </TableCell>
                                    </TableRow>}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleUpdateAll} variant="contained" color="primary" sx={{ textTransform: "none", margin: "0px 50px 50px 0px" }}>Update All</Button>

            </DialogActions>
        </Dialog>
    );
};

export default BulkUpdateModal;
